import type { ReactNode } from 'react';

import { PageBuilderIcon } from '@fhs/cms-groq-queries';
import type { IconProps } from '@fhs/ui';
import * as Icons from '@fhs/ui/src/components/icon';

const NoopComponent: (props: IconProps) => ReactNode = () => null;

const iconNameToComponentMap: Record<PageBuilderIcon, (props: IconProps) => ReactNode> = {
  accessible: Icons.IconAccessible,
  account: Icons.IconAccount,
  add: Icons.IconAdd,
  'arrow-back': Icons.IconArrowBack,
  'arrow-forward': Icons.IconArrowForward,
  'arrow-right': Icons.ArrowRight,
  'arrow-up': Icons.IconArrowUp,
  bookmark: Icons.IconBookmark,
  'calendar-next-fill': Icons.IconCalendarNextFill,
  calendar: Icons.IconCalendar,
  camera: Icons.IconCamera,
  'cart-fill': Icons.IconCartFill,
  'cart-outline': Icons.IconCartOutline,
  catering: Icons.IconCatering,
  'check-circle-outline': Icons.IconCheckCircleOutline,
  'check-fill': Icons.IconCheckFill,
  check: Icons.IconCheck,
  'chevron-down': Icons.IconChevronDown,
  'chevron-left': Icons.IconChevronLeft,
  'chevron-right': Icons.IconChevronRight,
  'chevron-up': Icons.IconChevronUp,
  close: Icons.IconClose,
  copy: Icons.IconCopy,
  curbside: Icons.IconCurbside,
  delete: Icons.IconDelete,
  delivery: Icons.IconDelivery,
  dialog: Icons.IconDialog,
  'dine-in': Icons.IconDineIn,
  dollar: Icons.IconDollar,
  'drive-thru': Icons.IconDriveThru,
  drive: Icons.IconDrive,
  earn: Icons.IconEarn,
  edit: Icons.IconEdit,
  'error-fill': Icons.IconErrorFill,
  error: Icons.IconError,
  facebook: Icons.IconFacebook,
  'favorite-fill': Icons.IconFavoriteFill,
  'favorite-outline': Icons.IconFavoriteOutline,
  filter: Icons.IconFilter,
  'fork-axe': Icons.IconForkAxe,
  'hat-fill': Icons.IconHatFill,
  'hat-outline': Icons.IconHatOutline,
  help: Icons.IconHelp,
  'home-fill': Icons.IconHomeFill,
  'home-outline': Icons.IconHomeOutline,
  image: Icons.IconImage,
  'info-fill': Icons.IconInfoFill,
  info: Icons.IconInfo,
  instagram: Icons.IconInstagram,
  language: Icons.IconLanguage,
  laptop: Icons.IconLaptop,
  list: Icons.IconList,
  'location-fill': Icons.IconLocationFill,
  'location-outline': Icons.IconLocationOutline,
  locked: Icons.IconLocked,
  'mail-fill': Icons.IconMailFilled,
  mail: Icons.IconMail,
  map: Icons.IconMap,
  menu: Icons.IconMenu,
  mobile: Icons.IconMobile,
  monitor: Icons.IconMonitor,
  'more-vertical': Icons.IconMoreVertical,
  more: Icons.IconMore,
  'near-me': Icons.IconNearMe,
  'offer-outline': Icons.IconOfferOutline,
  offer: Icons.IconOffer,
  'payment-type-add-new-payment': Icons.IconPaymentTypeAddNewPayment,
  'payment-type-amazon-pay': Icons.IconPaymentTypeAmazonPay,
  'payment-type-amex': Icons.IconPaymentTypeAmex,
  'payment-type-apple-pay': Icons.IconPaymentTypeApplePay,
  'payment-type-cash': Icons.IconPaymentTypeCash,
  'payment-type-diners-club': Icons.IconPaymentTypeDinersClub,
  'payment-type-discover': Icons.IconPaymentTypeDiscover,
  'payment-type-generic-credit-card': Icons.IconPaymentTypeGenericCreditCard,
  'payment-type-google-pay': Icons.IconPaymentTypeGooglePay,
  'payment-type-jcb': Icons.IconPaymentTypeJcb,
  'payment-type-mastercard': Icons.IconPaymentTypeMastercard,
  'payment-type-paypal': Icons.IconPaymentTypePaypal,
  'payment-type-visa': Icons.IconPaymentTypeVisa,
  'qr-scan': Icons.IconQrScan,
  qr: Icons.IconQr,
  'receipt-long': Icons.IconReceiptLong,
  redeem: Icons.IconRedeem,
  refresh: Icons.IconRefresh,
  'remove-circle-outline': Icons.IconRemoveCircleOutline,
  remove: Icons.IconRemove,
  'rewards-and-offers': Icons.IconRewardsAndOffers,
  'rewards-fill': Icons.IconRewardsFill,
  'rewards-outline': Icons.IconRewardsOutline,
  'rewards-trophy': Icons.IconRewardsTrophy,
  rewards: Icons.IconRewards,
  search: Icons.IconSearch,
  settings: Icons.IconSettings,
  'shopping-bag': Icons.IconShoppingBag,
  sodium: Icons.IconSodium,
  'star-rate-fill': Icons.IconStarRateFill,
  'star-rate': Icons.IconStarRate,
  store: Icons.IconStore,
  telephone: Icons.IconTelephone,
  'tik-tok': Icons.IconTikTok,
  time: Icons.IconTime,
  twitter: Icons.IconTwitter,
  unlocked: Icons.IconUnlocked,
  'visibility-off': Icons.IconVisibilityOff,
  visibility: Icons.IconVisibility,
  voice: Icons.IconVoice,
  'warning-fill': Icons.IconWarningFill,
  'you-tube': Icons.IconYouTube,
};

type Props = IconProps & {
  name: PageBuilderIcon;
};

export function Icon({ name, ...iconProps }: Props) {
  const IconComponent = iconNameToComponentMap[name] ?? NoopComponent;

  return <IconComponent {...iconProps} />;
}
